import { TenantBaseApi } from "api/TenantSlug/TenantBaseApi.ts";

import GetIncomingPostbacksResponse = App.Http.Responses.Tracking.GetIncomingPostbacksResponse;
import GetIncomingPostbackResponse = App.Http.Responses.Tracking.GetIncomingPostbackResponse;
import StoreIncomingPostbackRequest = App.Http.Requests.Tracking.StoreIncomingPostbackRequest;
import GetIncomingPostbacksRequest = App.Http.Requests.Tracking.GetIncomingPostbacksRequest;
import UpdateIncomingPostbackRequest = App.Http.Requests.Tracking.UpdateIncomingPostbackRequest;
import GetConnectionSequenceStepsExportResponse = App.Http.Responses.ConnectionCreator.SequenceStep.GetConnectionSequenceStepsExportResponse;
import StoreIncomingPostbackConnectionSequenceStepsRequest = App.Http.Requests.Tracking.StoreIncomingPostbackConnectionSequenceStepsRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import TestIncomingPostbackRequest = App.Http.Requests.Tracking.TestIncomingPostbackRequest;
import TestConnectionSequenceStepResponse = App.Http.Responses.ConnectionSequenceStep.TestConnectionSequenceStepResponse;

class IncomingPostbackApi extends TenantBaseApi {
    async index(request: GetIncomingPostbacksRequest): Promise<GetIncomingPostbacksResponse> {
        return await this.get<GetIncomingPostbacksResponse, GetIncomingPostbacksRequest>("/incoming-postbacks", {
            params: request,
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async show(postbackId: string, request: any): Promise<GetIncomingPostbackResponse> {
        return await this.get<GetIncomingPostbackResponse, unknown>(`/incoming-postbacks/${postbackId}`, {
            params: request,
        });
    }

    async store(request: StoreIncomingPostbackRequest): Promise<GetIncomingPostbackResponse> {
        return await this.post<GetIncomingPostbackResponse, StoreIncomingPostbackRequest>(
            "/incoming-postbacks",
            request
        );
    }

    async update(request: UpdateIncomingPostbackRequest, postbackId: string): Promise<GetIncomingPostbackResponse> {
        return await this.patch<GetIncomingPostbackResponse, UpdateIncomingPostbackRequest>(
            `/incoming-postbacks/${postbackId}`,
            request
        );
    }

    async destroy(postbackId: string): Promise<void> {
        return await this.delete<void>(`/incoming-postbacks/${postbackId}`);
    }

    /**
     * Get specific incoming postback in export format
     *
     * - Request: { id: string }
     * - Response: GetIncomingPostbackResponse
     */
    async showExport(
        incomingPostbackId: string,
        request?: GetIncomingPostbacksRequest
    ): Promise<GetIncomingPostbackResponse> {
        return await this.get<GetIncomingPostbackResponse, GetIncomingPostbacksRequest>(
            `/incoming-postbacks/${incomingPostbackId}/export`,
            {
                params: request,
            }
        );
    }

    /**
     * Get specific incoming postback connection sequence steps in export format
     *
     * - Request: { id: string }
     * - Response: GetIncomingPostbackExportResponse
     */
    async showMethodsExport(
        incomingPostbackId: string,
        request?: GetIncomingPostbacksRequest
    ): Promise<GetConnectionSequenceStepsExportResponse> {
        return await this.get<GetConnectionSequenceStepsExportResponse, GetIncomingPostbacksRequest>(
            `/incoming-postbacks/${incomingPostbackId}/connection-sequence-steps/export`,
            {
                params: request,
            }
        );
    }

    /**
     * Update IncomingPostback methods (PUT)
     *
     * - Request: StoreIncomingPostbackConnectionSequenceStepsRequest
     * - Response: GetConnectionSequenceStepsExportResponse
     */
    async updateMethods(
        request: StoreIncomingPostbackConnectionSequenceStepsRequest,
        incomingPostbackId: string
    ): Promise<GetConnectionSequenceStepsExportResponse> {
        return await this.put<
            GetConnectionSequenceStepsExportResponse,
            StoreIncomingPostbackConnectionSequenceStepsRequest
        >(`/incoming-postbacks/${incomingPostbackId}/connection-sequence-steps`, request);
    }

    async export(request: BaseExportRequest): Promise<ExportResponse> {
        return await this.post<ExportResponse, BaseExportRequest>("/incoming-postbacks/export", request);
    }

    async test(incomingPostbackId: string, request: TestIncomingPostbackRequest): Promise<TestConnectionSequenceStepResponse> {
        return await this.post<TestConnectionSequenceStepResponse, TestIncomingPostbackRequest>(
            `/incoming-postbacks/${incomingPostbackId}/test`,
            request
        );
    }
}

export const incomingPostbackApi = new IncomingPostbackApi();
